import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../lib/firebase";
import { Radio, Music, Clock, ChevronLeft, Play, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useRadioStore } from "../stores/radioStore";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatDuration(seconds) {
  if (!seconds) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function WeekCalendar({ schedules }) {
  const today = new Date().getDay();
  return (
    <div className="grid grid-cols-7 gap-1.5">
      {DAYS_SHORT.map((day, i) => {
        const daySchedules = schedules.filter((s) => s.dayOfWeek === i);
        const isToday = i === today;
        return (
          <div key={day} className={`rounded-xl p-2 min-h-[100px] border ${isToday ? "border-[#B026FF]/50 bg-[#B026FF]/5" : "border-white/5 bg-[#111]"}`}>
            <p className={`text-xs font-bold mb-2 ${isToday ? "text-[#B026FF]" : "text-zinc-500"}`}>{day}</p>
            <div className="space-y-1">
              {daySchedules.length === 0 && (
                <p className="text-[9px] text-zinc-700">—</p>
              )}
              {daySchedules.map((s, j) => (
                <div key={j} className="text-[9px] bg-[#B026FF]/20 text-[#B026FF] px-1.5 py-1 rounded">
                  <p className="font-bold">{s.startTime}</p>
                  <p className="truncate opacity-75">{s.showTitle}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function StationPage() {
  const { stationId } = useParams();
  const { isCreator, user } = useAuth();
  const { playStation, playing, currentStation, stop } = useRadioStore();
  const [station, setStation] = useState(null);
  const [shows, setShows] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tuningIn, setTuningIn] = useState(false);

  const isThisStationPlaying = playing && currentStation?.id === stationId;

  const handleTuneIn = async () => {
    if (isThisStationPlaying) {
      stop();
      return;
    }
    setTuningIn(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (user) {
        const token = await user.getIdToken(true);
        headers["Authorization"] = `Bearer ${token}`;
      }
      const res = await fetch(`${API_URL}/api/radio/playback-plan/${stationId}`, { headers });
      if (!res.ok) throw new Error("Could not load playback plan");
      const plan = await res.json();
      await playStation({ ...station, id: stationId }, plan);
    } catch (e) {
      console.error("Tune in failed:", e);
      // Play station without plan (stream URL fallback)
      await playStation({ ...station, id: stationId }, null);
    }
    setTuningIn(false);
  };

  useEffect(() => {
    loadStation();
  }, [stationId]);

  const loadStation = async () => {
    try {
      const stationDoc = await getDoc(doc(db, "stations", stationId));
      if (!stationDoc.exists()) { setLoading(false); return; }
      const stationData = { id: stationDoc.id, ...stationDoc.data() };
      setStation(stationData);

      // Load creator
      const creatorDoc = await getDoc(doc(db, "creators", stationData.creatorId));
      if (creatorDoc.exists()) setCreator({ id: creatorDoc.id, ...creatorDoc.data() });

      // Load shows
      const showsQ = query(collection(db, "shows"), where("stationId", "==", stationId), orderBy("createdAt", "desc"));
      const showsSnap = await getDocs(showsQ);
      setShows(showsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      // Load schedules
      const schedulesQ = query(collection(db, "schedules"), where("stationId", "==", stationId), orderBy("dayOfWeek", "asc"));
      const schedulesSnap = await getDocs(schedulesQ);
      setSchedules(schedulesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#B026FF] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!station) return (
    <div className="text-center py-20">
      <p className="text-zinc-500">Station not found.</p>
      <Link to="/radio" className="text-[#B026FF] text-sm mt-2 inline-block">← Back to Radio</Link>
    </div>
  );

  return (
    <div data-testid="station-page" className="space-y-8 animate-fade-in">
      <Link to="/radio" className="flex items-center gap-1 text-zinc-500 hover:text-white text-sm transition-colors">
        <ChevronLeft size={16} /> Back to Radio
      </Link>

      {/* Station Header */}
      <div className="bg-[#111] border border-white/5 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className="w-24 h-24 rounded-2xl bg-[#1a1a1a] overflow-hidden flex-shrink-0">
            {station.logoURL ? (
              <img src={station.logoURL} alt={station.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Radio size={28} className="text-zinc-600" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {station.genre && <span className="text-[10px] bg-[#B026FF]/20 text-[#B026FF] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">{station.genre}</span>}
              {station.autodjEnabled && <span className="text-[10px] bg-green-500/20 text-green-400 font-bold px-2 py-0.5 rounded-full">Auto-DJ</span>}
            </div>
            <h1 className="text-2xl font-black text-white mb-1">{station.name}</h1>
            <p className="text-zinc-400 text-sm mb-3">{station.description}</p>
            {creator && (
              <Link to={`/c/${creator.slug}`} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors">
                <div className="w-6 h-6 rounded-full bg-[#1a1a1a] overflow-hidden">
                  {creator.photoURL ? <img src={creator.photoURL} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#B026FF]/20" />}
                </div>
                <span>{creator.displayName}</span>
              </Link>
            )}
          </div>
          {user && station.creatorId === user.uid && (
            <Link to="/dashboard/station" className="text-xs bg-[#B026FF] text-white font-bold px-4 py-2 rounded-full hover:bg-[#9b1ee6] transition-colors flex-shrink-0">
              Manage Station
            </Link>
          )}
          {/* Tune In Button */}
          <button
            data-testid="tune-in-btn"
            onClick={handleTuneIn}
            disabled={tuningIn}
            className={`flex items-center gap-2 font-bold text-sm px-5 py-2.5 rounded-full transition-all flex-shrink-0 disabled:opacity-60 ${
              isThisStationPlaying
                ? "bg-white/10 text-white border border-white/20 hover:bg-red-500/20 hover:text-red-400 hover:border-red-400/30"
                : "text-black hover:scale-105"
            }`}
            style={!isThisStationPlaying ? { background: "linear-gradient(135deg, #B026FF, #7C3AED)" } : {}}
          >
            {tuningIn ? (
              <Loader2 size={14} className="animate-spin text-white" />
            ) : isThisStationPlaying ? (
              <>
                <span className="w-2 h-2 rounded-full bg-[#00ff77] animate-pulse" />
                <span className="text-white">Live</span>
              </>
            ) : (
              <>
                <Play size={14} className="text-white" fill="white" />
                <span className="text-white">Tune In</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* 7-Day Schedule Calendar */}
      <section>
        <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
          <Clock size={16} className="text-[#B026FF]" />
          7-Day Schedule
        </h2>
        <WeekCalendar schedules={schedules} />
      </section>

      {/* Shows */}
      <section>
        <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
          <Music size={16} className="text-[#B026FF]" />
          Shows ({shows.length})
        </h2>
        {shows.length > 0 ? (
          <div className="space-y-3">
            {shows.map((show) => (
              <div key={show.id} data-testid={`show-card-${show.id}`} className="bg-[#111] border border-white/5 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-1">{show.title}</h3>
                    <p className="text-zinc-500 text-xs mb-2">{show.description}</p>
                    <div className="flex items-center gap-3 text-zinc-600 text-xs">
                      <span>{DAYS[show.dayOfWeek]} at {show.startTime}</span>
                      <span className="flex items-center gap-1"><Clock size={10} /> {Math.floor((show.totalRuntime || 0) / 60)}m</span>
                      <span className="flex items-center gap-1"><Music size={10} /> {(show.tracks || []).length} tracks</span>
                    </div>
                  </div>
                </div>
                {show.tracks && show.tracks.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5">
                    {show.tracks.map((track, ti) => (
                      <div key={ti} className="flex items-center gap-3 text-xs text-zinc-400">
                        <span className="text-zinc-600 w-5 text-right">{ti + 1}</span>
                        <Play size={10} className="text-zinc-600 flex-shrink-0" />
                        <span className="flex-1 truncate">{track.title}</span>
                        <span className="text-zinc-600">{formatDuration(track.durationSeconds)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10 text-zinc-600 text-sm">No shows scheduled yet.</div>
        )}
      </section>
    </div>
  );
}
